import React, { useState, useEffect } from 'react';
import { AlertCircle, WifiOff, RefreshCw, CheckCircle } from 'lucide-react';
import { createConnectionMonitor, detectNetworkStatus, resetConnectionCache } from '../lib/supabase';

const ConnectionStatus = () => {
  const [connectionStatus, setConnectionStatus] = useState({
    connected: null,
    error: null,
    suggestion: null,
    errorCode: null,
    loading: true
  });
  const [monitor, setMonitor] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Initialize lightweight monitoring
  useEffect(() => {
    const connectionMonitor = createConnectionMonitor((status) => {
      setConnectionStatus(prevStatus => {
        // FIXED: Access prev properly within setState callback
        const wasDisconnected = prevStatus?.connected === false;
        const isNowConnected = status?.connected === true;
        
        // Show success message when connection recovers
        if (isNowConnected && wasDisconnected) {
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 3000);
        }
        
        return {
          ...status,
          loading: false
        };
      });
    });
    
    setMonitor(connectionMonitor);
    connectionMonitor?.start();

    return () => {
      connectionMonitor?.stop();
    };
  }, []);

  // Manual retry with cache reset
  const handleRetry = async () => {
    setIsRetrying(true);
    setConnectionStatus(prev => ({ ...prev, loading: true }));
    
    try {
      console.log('🔄 Manual retry initiated...');
      
      // Check network first
      const networkStatus = await detectNetworkStatus();
      if (!networkStatus?.online) {
        setConnectionStatus({
          connected: false,
          error: networkStatus?.error,
          suggestion: networkStatus?.suggestion,
          loading: false,
          errorCode: networkStatus?.errorCode
        });
        return;
      }
      
      // Clear cache and force fresh check
      resetConnectionCache();
      
      const status = await monitor?.forceCheck();
      if (status) {
        setConnectionStatus({
          ...status,
          loading: false
        });
      }
      
    } catch (error) {
      console.error('❌ Manual retry failed:', error);
      setConnectionStatus({
        connected: false,
        error: error?.message || 'Manual retry failed',
        suggestion: 'Try refreshing the page.',
        loading: false,
        errorCode: 'RETRY_FAILED'
      });
    } finally {
      setIsRetrying(false);
    }
  };

  // Success message (brief)
  if (showSuccess) {
    return (
      <div className="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg max-w-sm">
        <div className="flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <div>
            <p className="font-medium text-green-800">Connection Restored</p>
            <p className="text-sm text-green-600">System is back online</p>
          </div>
        </div>
      </div>
    );
  }

  // Don't show anything if connection is healthy and not loading
  if (connectionStatus?.connected === true && !connectionStatus?.loading) {
    return null;
  }

  // Loading state (simplified)
  if (connectionStatus?.loading) {
    return (
      <div className="fixed top-4 right-4 z-50 bg-blue-50 border border-blue-200 rounded-lg p-3 shadow-lg">
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
          <p className="text-sm font-medium text-blue-800">
            {isRetrying ? 'Retrying...' : 'Testing connection...'}
          </p>
        </div>
      </div>
    );
  }

  // Error state (streamlined)
  const isNetworkIssue = connectionStatus?.errorCode?.includes('NETWORK') || 
                        connectionStatus?.errorCode?.includes('TIMEOUT') ||
                        connectionStatus?.errorCode?.includes('NO_INTERNET');
  
  return (
    <div className="fixed top-4 right-4 z-50 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg max-w-sm">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          {isNetworkIssue ? (
            <WifiOff className="w-5 h-5 text-red-600" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-medium mb-1 text-red-800">
            {isNetworkIssue ? 'Connection Problem' : 'Database Issue'}
          </h3>
          
          <p className="text-sm mb-2 text-red-600">
            {connectionStatus?.error}
          </p>
          
          {connectionStatus?.suggestion && (
            <p className="text-xs mb-3 opacity-75 text-red-500">
              {connectionStatus?.suggestion}
            </p>
          )}
          
          {connectionStatus?.responseTime && (
            <p className="text-xs mb-2 text-gray-500">
              Response: {connectionStatus?.responseTime}
            </p>
          )}
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className="inline-flex items-center px-3 py-1 text-xs font-medium border rounded hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed text-red-700 bg-red-100 border-red-200"
            >
              {isRetrying ? (
                <>
                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                  Retrying
                </>
              ) : (
                <>
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Try Again
                </>
              )}
            </button>
            
            <button
              onClick={() => window.location?.reload()}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded hover:bg-gray-200"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionStatus;
